// /settings (admin): tasks, stations, shelves and their levels, rule
// switches, prompts, board slides, the planner's schedule, the buffer and
// score weights, and roles. Every save asks for a reason; the API audits it.
// Facts are rules; how much to keep on the shelves is the ratio and the score,
// so there is no "add a buffer rule" here on purpose.
import { useIonToast } from "@ionic/react";
import { useEffect, useState } from "react";
import type { PersonRow, Role, RuleRow, SettingRow, ShelfRow, StationRow, TaskRow } from "../lib/types";
import { BuildFlow, ShelfGauge } from "../design";
import { announce, can, useApi, useMe } from "../api/live";
import { ApiError, put } from "../api/client";
import { paths } from "../api/paths";
import { stamp } from "../lib/days";
import { useTasks } from "../lib/useRegister";
import { Page } from "../shell/Page";
import { ErrorNote, Loading, useReason } from "../shell/ui";
import { list } from "../lib/safe";

const TABS = { register: "Tasks and stations", shelves: "Shelves", rules: "Rules", planner: "Planner", prompts: "Prompts", board: "Board", roles: "Roles" } as const;
type Tab = keyof typeof TABS;

/** The 'schedule', 'engine', 'prompts' and 'board' settings as the API keeps them (api/src/data/settings.ts). */
type Obj = Record<string, unknown>;
type PromptRow = { on: boolean; time: string; days: string[]; to?: string[] };
type Prompts = { enabled: boolean; prompts: Record<string, PromptRow>; quiet: string[]; appUrl: string };
type Board = { slides: { id: string; on: boolean; seconds?: number }[]; seconds: number };
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const obj = (v: unknown): Obj => (v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : {});
const getAt = (o: unknown, path: string[]): unknown => path.reduce<unknown>((x, k) => obj(x)[k], o);
function setAt(o: Obj, path: string[], v: unknown): Obj {
  const [k, ...rest] = path;
  return { ...o, [k]: rest.length ? setAt(obj(o[k]), rest, v) : v };
}

/** PUT with a reason; toasts the outcome. */
function useSave() {
  const ask = useReason();
  const [present] = useIonToast();
  return async (what: string, path: string, body: Record<string, unknown>, entity: string) => {
    const reason = await ask(`Save ${what}`, "Settings changes are audited. Why this change?");
    if (!reason) return false;
    try {
      await put(path, { ...body, reason });
      present({ message: `${what[0].toUpperCase()}${what.slice(1)} saved`, duration: 2000, color: "success" });
      announce([entity]);
      return true;
    } catch (e) { present({ message: (e as ApiError).message, duration: 4000, color: "danger" }); return false; }
  };
}

export function SettingsPage() {
  const { me } = useMe();
  const [tab, setTab] = useState<Tab>("register");
  const settings = useApi<SettingRow[]>(can(me, "admin") ? paths.settings() : null, ["setting"]);
  if (!can(me, "admin")) return <Page title="Settings"><p className="empty">Settings are for admins. Ask Andrea or James.</p></Page>;
  const value = <T,>(key: string, fallback: T): T => (settings.data?.find((s) => s.key === key)?.value as T) ?? fallback;
  const updated = (key: string) => settings.data?.find((s) => s.key === key)?.updatedAt ?? undefined;
  return (
    <Page title="Settings">
      <div className="tabs" role="tablist">
        {(Object.keys(TABS) as Tab[]).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={`btn btn--sm${tab === t ? " btn--primary" : ""}`} onClick={() => setTab(t)}>{TABS[t]}</button>
        ))}
      </div>
      {!settings.data ? (settings.error ? <ErrorNote error={settings.error} retry={settings.reload} /> : <Loading />) : (
        tab === "register" ? <RegisterTab />
          : tab === "shelves" ? <ShelvesTab />
          : tab === "rules" ? <RulesTab />
          : tab === "planner" ? <PlannerTab schedule={obj(value("schedule", {}))} engine={obj(value("engine", {}))} />
          : tab === "prompts" ? <PromptsTab prompts={value<Prompts | null>("prompts", null)} updated={updated("prompts")} />
          : tab === "board" ? <BoardTab board={value<Board | null>("board", null)} />
          : <RolesTab />
      )}
    </Page>
  );
}

function RegisterTab() {
  const { tasks } = useTasks();
  const shelves = useApi<ShelfRow[]>(paths.shelves(), ["shelf", "stock"]);
  const stations = useApi<StationRow[]>(paths.stations(), ["station"]);
  const save = useSave();
  const [sizes, setSizes] = useState<Record<string, string>>({});
  return (
    <div className="stack">
      <section className="card">
        <div className="card__head"><h2>The build flow</h2><span className="small muted">Drawn from the tasks and shelves below</span></div>
        {shelves.data ? <BuildFlow tasks={tasks} shelves={shelves.data} /> : <Loading />}
      </section>
      <section className="card scroll-x">
        <div className="card__head"><h2>Tasks</h2><span className="small muted">Saved on the current register revision, with a reason</span></div>
        <table className="table">
          <thead><tr><th>Task</th><th>Group</th><th>Steps</th><th className="num">Units a session</th><th /></tr></thead>
          <tbody>{list(tasks).map((t: TaskRow) => (
            <tr key={t.code}><td><b>{t.code}</b> {t.name}</td><td>{t.group}</td><td className="small">{t.steps.map((s) => s.code).join(" → ")}</td>
              <td className="num"><input className="input input--sm input--num" inputMode="numeric" aria-label={`${t.code} units a session`} value={sizes[t.code] ?? String(t.sessionQty)} onChange={(e) => setSizes({ ...sizes, [t.code]: e.target.value.replace(/\D/g, "") })} /></td>
              <td>{sizes[t.code] != null && Number(sizes[t.code]) !== t.sessionQty ? <button type="button" className="btn btn--sm btn--primary" onClick={() => save(`${t.code}'s session size`, paths.task(t.code), { sessionQty: Number(sizes[t.code]) }, "task")}>Save</button> : null}</td></tr>
          ))}</tbody>
        </table>
      </section>
      <section className="card scroll-x">
        <div className="card__head"><h2>Stations</h2><span className="small muted">Named so plan cells and e-paper tags can point at them</span></div>
        {stations.data ? (
          <table className="table"><thead><tr><th>Station</th><th>Floor</th><th>Steps it hosts</th><th>Tag</th></tr></thead>
            <tbody>{list(stations.data).map((s) => <tr key={s.id}><td><b>{s.name}</b></td><td>{s.floor}</td><td>{s.steps.join(", ")}</td><td>{s.tag ? <code>{s.tag}</code> : "—"}</td></tr>)}</tbody></table>
        ) : stations.error ? <p className="small muted">Stations aren't named yet.</p> : <Loading />}
      </section>
    </div>
  );
}

function ShelvesTab() {
  const shelves = useApi<ShelfRow[]>(paths.shelves(), ["shelf", "stock"]);
  const save = useSave();
  const [edits, setEdits] = useState<Record<string, Partial<Record<"critical" | "min" | "recommended", string>>>>({});
  if (!shelves.data) return <Loading />;
  const field = (s: ShelfRow, k: "critical" | "min" | "recommended") => (
    <input className="input input--sm input--num" inputMode="numeric" aria-label={`${s.code} ${k}`} value={edits[s.code]?.[k] ?? (s[k] ?? "")}
      onChange={(e) => setEdits({ ...edits, [s.code]: { ...edits[s.code], [k]: e.target.value.replace(/\D/g, "") } })} />
  );
  return (
    <section className="card scroll-x">
      <div className="card__head"><h2>Shelves and their levels</h2><span className="small muted">Critical, Min and Recommended. The ratio does the rest.</span></div>
      <table className="table">
        <thead><tr><th>Shelf</th><th className="num">Critical</th><th className="num">Min</th><th className="num">Recommended</th><th>Now</th><th /></tr></thead>
        <tbody>{shelves.data.filter((s) => !s.isOutput && s.countMode === "number").map((s) => {
          const e = edits[s.code];
          return (
            <tr key={s.code}><td><b>{s.code}</b> {s.name}<div className="small muted">{s.line} · {s.floor}</div></td>
              <td className="num">{field(s, "critical")}</td><td className="num">{field(s, "min")}</td><td className="num">{field(s, "recommended")}</td>
              <td style={{ minWidth: 200 }}><ShelfGauge shelf={s} variant="compact" /></td>
              <td>{e ? <button type="button" className="btn btn--sm btn--primary" onClick={async () => {
                const body = Object.fromEntries(Object.entries(e).map(([k, v]) => [k, v === "" ? null : Number(v)]));
                if (await save(`${s.code}'s levels`, paths.shelf(s.code), body, "shelf")) setEdits(({ [s.code]: _, ...rest }) => rest);
              }}>Save</button> : null}</td></tr>
          );
        })}</tbody>
      </table>
    </section>
  );
}

function RulesTab() {
  const rules = useApi<RuleRow[]>(paths.rules(), ["register", "setting"]);
  const save = useSave();
  const [draft, setDraft] = useState<RuleRow[]>([]);
  useEffect(() => setDraft(list(rules.data)), [rules.data]);
  if (!rules.data) return rules.error ? <ErrorNote error={rules.error} retry={rules.reload} /> : <Loading />;
  const was = new Map(rules.data.map((r) => [r.code, r]));
  const changed = draft.filter((r) => was.get(r.code)?.enabled !== r.enabled || was.get(r.code)?.kind !== r.kind);
  const set = (i: number, p: Partial<RuleRow>) => setDraft(draft.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <section className="card">
      <div className="card__head"><h2>Rule switches</h2><span className="small muted">Rules are code; their switches are data, on the current register revision</span></div>
      {draft.length ? draft.map((r, i) => (
        <div key={r.code} className="settings-row">
          <label className="switch"><input type="checkbox" checked={r.enabled} aria-label={`${r.code} on`} onChange={(e) => set(i, { enabled: e.target.checked })} /><span /></label>
          <span className="settings-row__k"><b>{r.code}</b> {r.name}<small>{[r.says, r.setting].filter(Boolean).join(" · ")}</small></span>
          <select className="select select--inline" aria-label={`${r.code} type`} value={r.kind} onChange={(e) => set(i, { kind: e.target.value as RuleRow["kind"] })}>
            <option value="block">block</option><option value="warn">warn</option><option value="advisory">advisory</option><option value="info">info</option>
          </select>
        </div>
      )) : <p className="small muted">The register has no rules yet.</p>}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-3)" }}>
        <button type="button" className="btn btn--primary" disabled={!changed.length} onClick={async () => {
          if (await save("the rule switches", paths.rules(), { rules: changed.map((r) => ({ code: r.code, enabled: r.enabled, kind: r.kind })) }, "register")) rules.reload();
        }}>Save</button>
      </div>
    </section>
  );
}

type Field = { path: string[]; label: string; kind: "number" | "time" | "day" };

/** A card of fields inside one setting's value; Save writes the whole value back. */
function FieldsCard({ title, hint, settingKey, value, fields }: { title: string; hint: string; settingKey: string; value: Obj; fields: Field[] }) {
  const save = useSave();
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const changed = JSON.stringify(draft) !== JSON.stringify(value);
  return (
    <section className="card">
      <div className="card__head"><h2>{title}</h2><span className="small muted">{hint}</span></div>
      {fields.map((f) => {
        const v = getAt(draft, f.path);
        const label = f.label;
        return (
          <div key={f.path.join(".")} className="settings-row">
            <span className="settings-row__k">{label}</span>
            {f.kind === "day" ? (
              <select className="select select--inline" aria-label={label} value={String(v ?? "")} onChange={(e) => setDraft(setAt(draft, f.path, e.target.value))}>
                {WEEKDAYS.map((d) => <option key={d}>{d}</option>)}
              </select>
            ) : f.kind === "time" ? (
              <input className="input input--sm" type="time" aria-label={label} value={String(v ?? "")} onChange={(e) => setDraft(setAt(draft, f.path, e.target.value))} />
            ) : (
              <input className="input input--sm input--num" style={{ width: 96 }} inputMode="decimal" aria-label={label} value={v == null ? "" : String(v)}
                onChange={(e) => { const n = Number(e.target.value); setDraft(setAt(draft, f.path, e.target.value === "" || Number.isNaN(n) ? 0 : n)); }} />
            )}
          </div>
        );
      })}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-3)" }}>
        <button type="button" className="btn btn--primary" disabled={!changed} onClick={() => save(title.toLowerCase(), paths.setting(settingKey), { value: draft }, "setting")}>Save</button>
      </div>
    </section>
  );
}

const WEIGHTS: Record<string, string> = {
  devices: "Per device built", swing: "Per device of day-to-day swing", shelfDistance: "Per unit away from a shelf's path",
  changedCells: "Per cell changed from the approved plan", sameJobRunning: "Same job two days running", anchorLearners: "Extra learner on one anchor",
  floatHalf: "Empty half-day of someone who's in"
};

function PlannerTab({ schedule, engine }: { schedule: Obj; engine: Obj }) {
  return (
    <div className="grid-2">
      <FieldsCard title="The planner's schedule" hint="Europe/London time; no deploy needed" settingKey="schedule" value={schedule} fields={[
        { path: ["draft", "day"], label: "Next week's draft: day", kind: "day" }, { path: ["draft", "time"], label: "Next week's draft: time", kind: "time" },
        { path: ["autoApprove", "day"], label: "An untouched draft is approved: day", kind: "day" }, { path: ["autoApprove", "time"], label: "An untouched draft is approved: time", kind: "time" },
        { path: ["proposal", "time"], label: "Proposal from the latest count", kind: "time" },
        { path: ["lockAfterMinutes"], label: "A half-day locks this many minutes after it starts", kind: "number" },
        { path: ["halves", "AM", "start"], label: "Morning starts", kind: "time" }, { path: ["halves", "AM", "end"], label: "Morning ends", kind: "time" },
        { path: ["halves", "PM", "start"], label: "Afternoon starts", kind: "time" }, { path: ["halves", "PM", "end"], label: "Afternoon ends", kind: "time" }
      ]} />
      <FieldsCard title="The buffer and the score" hint="One control law per shelf; how plans are ranked" settingKey="engine" value={engine} fields={[
        { path: ["buffer", "shareWhenLow"], label: "Share of feeder output kept at or below Min", kind: "number" },
        { path: ["buffer", "drawWhenHigh"], label: "Above Recommended: draw down per day (0 holds)", kind: "number" },
        ...Object.keys(obj(engine.weights)).map((k): Field => ({ path: ["weights", k], label: `Score: ${WEIGHTS[k] ?? k}`, kind: "number" })),
        { path: ["learnerFactor"], label: "A learner's share of a session", kind: "number" },
        { path: ["encapBatch"], label: "Encapsulation batch", kind: "number" }
      ]} />
    </div>
  );
}

function PromptsTab({ prompts, updated }: { prompts: Prompts | null; updated?: string }) {
  const save = useSave();
  const [draft, setDraft] = useState(prompts);
  useEffect(() => setDraft(prompts), [prompts]);
  if (!draft) return <p className="empty">No prompt settings yet.</p>;
  const rows = Object.entries(obj(draft.prompts)) as [string, PromptRow][];
  const set = (id: string, p: Partial<PromptRow>) => setDraft({ ...draft, prompts: { ...draft.prompts, [id]: { ...draft.prompts[id], ...p } } });
  return (
    <section className="card">
      <div className="card__head"><h2>Daily prompts</h2><span className="small muted">Sent from planner@onlycat.com. In preview, only to the allowlist{updated ? ` · changed ${stamp(updated)}` : ""}</span></div>
      <div className="settings-row">
        <label className="switch"><input type="checkbox" checked={!!draft.enabled} aria-label="Prompts on" onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /><span /></label>
        <span className="settings-row__k"><b>Send prompts at all</b></span>
      </div>
      {rows.map(([id, p]) => (
        <div key={id} className="settings-row">
          <label className="switch"><input type="checkbox" checked={!!p.on} aria-label={`${id} on`} onChange={(e) => set(id, { on: e.target.checked })} /><span /></label>
          <span className="settings-row__k"><b style={{ textTransform: "capitalize" }}>{id}</b><small>{list(p.to).length ? `To ${list(p.to).join(", ")}` : "To everyone with prompts on"}</small></span>
          <input className="input input--sm" type="time" aria-label={`${id} time`} value={p.time ?? ""} onChange={(e) => set(id, { time: e.target.value })} />
          <div className="choices" role="group" aria-label={`${id} days`}>
            {WEEKDAYS.slice(0, 5).map((d) => (
              <button key={d} type="button" className="choice" aria-pressed={list(p.days).includes(d)}
                onClick={() => set(id, { days: list(p.days).includes(d) ? list(p.days).filter((x) => x !== d) : WEEKDAYS.filter((x) => x === d || list(p.days).includes(x)) })}>{d}</button>
            ))}
          </div>
        </div>
      ))}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-3)" }}>
        <button type="button" className="btn btn--primary" disabled={JSON.stringify(draft) === JSON.stringify(prompts)} onClick={() => save("the prompts", paths.setting("prompts"), { value: draft }, "setting")}>Save</button>
      </div>
    </section>
  );
}

function BoardTab({ board }: { board: Board | null }) {
  const save = useSave();
  const [draft, setDraft] = useState(board);
  useEffect(() => setDraft(board), [board]);
  if (!draft) return <p className="empty">No board settings yet.</p>;
  const slides = list(draft.slides);
  const setSlides = (n: Board["slides"]) => setDraft({ ...draft, slides: n });
  const move = (i: number, d: number) => { const n = [...slides]; const [x] = n.splice(i, 1); n.splice(Math.max(0, Math.min(n.length, i + d)), 0, x); setSlides(n); };
  return (
    <section className="card">
      <div className="card__head"><h2>Board slides</h2><span className="small muted">In this order; the week slide is the main one</span></div>
      <div className="settings-row">
        <span className="settings-row__k">Seconds a slide, unless it says otherwise</span>
        <input className="input input--sm input--num" inputMode="numeric" aria-label="Default seconds" value={draft.seconds ?? ""} onChange={(e) => setDraft({ ...draft, seconds: Number(e.target.value.replace(/\D/g, "") || 0) })} />
      </div>
      {slides.map((s, i) => (
        <div key={s.id} className="settings-row">
          <label className="switch"><input type="checkbox" checked={!!s.on} aria-label={`${s.id} on`} onChange={(e) => setSlides(slides.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))} /><span /></label>
          <span className="settings-row__k"><b style={{ textTransform: "capitalize" }}>{s.id}</b></span>
          <input className="input input--sm input--num" inputMode="numeric" aria-label={`${s.id} seconds`} placeholder={String(draft.seconds ?? "")} value={s.seconds ?? ""}
            onChange={(e) => { const t = e.target.value.replace(/\D/g, ""); setSlides(slides.map((x, j) => (j === i ? { ...x, seconds: t === "" ? undefined : Number(t) } : x))); }} />
          <span className="small muted">seconds</span>
          <button type="button" className="btn btn--sm" aria-label={`Move ${s.id} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
          <button type="button" className="btn btn--sm" aria-label={`Move ${s.id} down`} disabled={i === slides.length - 1} onClick={() => move(i, 1)}>↓</button>
        </div>
      ))}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-3)" }}>
        <button type="button" className="btn btn--primary" disabled={JSON.stringify(draft) === JSON.stringify(board)} onClick={() => save("the board slides", paths.setting("board"), { value: draft }, "setting")}>Save</button>
      </div>
    </section>
  );
}

function RolesTab() {
  const people = useApi<PersonRow[]>(paths.people(), ["person"]);
  const save = useSave();
  if (!people.data) return <Loading />;
  return (
    <section className="card scroll-x">
      <div className="card__head"><h2>People and roles</h2><span className="small muted">Operators read and log; supervisors plan and count; admins change settings</span></div>
      <table className="table">
        <thead><tr><th>Name</th><th>Email</th><th>Contract</th><th>Prompts</th><th>Role</th></tr></thead>
        <tbody>{people.data.map((p) => (
          <tr key={p.id}><td><b>{p.name}</b>{!p.plannable ? <div className="small muted">not planned</div> : null}</td><td className="small">{p.email}</td>
            <td className="small">{p.contractText ?? "—"}</td><td>{p.notify ? "yes" : "no"}</td>
            <td><select className="select select--inline" aria-label={`${p.name}'s role`} value={p.role} onChange={(e) => save(`${p.name}'s role`, paths.person(p.id), { role: e.target.value as Role }, "person")}>
              <option value="operator">operator</option><option value="supervisor">supervisor</option><option value="admin">admin</option>
            </select></td></tr>
        ))}</tbody>
      </table>
    </section>
  );
}
